import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    type DocsVerificationSideEnum,
    DocsVerificationFileSizeEnum,
    DocsVerificationTabEnum,
    DocsVerificationTypeEnum,
} from '@models/enums';
import { DocsVerificationStepEnum } from '@models/enums';
import type { FaceDetectResult, FileWithPreview } from '@models/types';
import { FileErrorCodeEnum } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/types/file-error-code-enum';
import type {
    UploadDocumentRequest,
    UploadLivenessDocumentRequest,
} from '@modules/docs-verification-widget/api';
import {
    useUploadDocumentSDKMutation,
    useUploadLivenessDocumentSDKMutation,
} from '@modules/docs-verification-widget/api';
import { uploadFileResponse } from '@modules/docs-verification-widget/demo-responses/upload-file-response';
import type { UploadDocumentResponse } from '@modules/docs-verification-widget/models/upload-document-response';
import { useAppDispatch, useAppSelector } from '@store';
import type { VerificationFileState } from '@store/slices/docs-verification/docs-verification-slice';
import { docsVerificationSlice } from '@store/slices/docs-verification/docs-verification-slice';
import { isValidExtension } from '@utils/files/util';
import { isFileSizeInRange, sleep } from '@utils/util';
import imageCompression from 'browser-image-compression';
import { v4 as uuidv4 } from 'uuid';

type UploadFileProps = {
    file: FileWithPreview | FileWithPreview[];
    tab: DocsVerificationTabEnum;
    documentType: DocsVerificationTypeEnum;
    faceDetectResult?: FaceDetectResult;
    side?: DocsVerificationSideEnum;
    metadata?: UploadLivenessDocumentRequest['body']['metadata'];
    screen_id?: string | null;
};

export const MAX_FILE_SIZE_KB = 12 * 1024;
export const MIN_FILE_SIZE_KB = 50;
export const MIN_POA_FILE_SIZE_KB = 1;

export const useUploadFile = (allowedExtensions?: string[]) => {
    const sdkOptions = useAppSelector((state) => state.docsVerification.sdkOptions);
    const [uploadDocumentSdk] = useUploadDocumentSDKMutation();
    const [uploadLivenessDocumentSdk] = useUploadLivenessDocumentSDKMutation();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const validateFileSize = useCallback(
        (size: number, minSize: number): VerificationFileState['status'] => {
            const resSizeFile = isFileSizeInRange(size, minSize, MAX_FILE_SIZE_KB);

            if (resSizeFile === DocsVerificationFileSizeEnum.SUCCESS) {
                return {
                    error: false,
                    success: false,
                    message: '',
                    loading: false,
                };
            }
            if (resSizeFile === DocsVerificationFileSizeEnum.BIG) {
                return {
                    success: false,
                    error: true,
                    message: 'validation.docSizeExceedsMaxLimit',
                    messageParams: { size: MAX_FILE_SIZE_KB / 1024 },
                    loading: false,
                };
            }

            return {
                error: true,
                success: false,
                message: 'validation.docSizeBelowMinLimit',
                messageParams: { size: minSize },
                loading: false,
            };
        },
        [],
    );

    const getSizeFile = useCallback(
        (file: FileWithPreview, tab: DocsVerificationTabEnum) => {
            const minSize =
                tab === DocsVerificationTabEnum.ADDRESS ? MIN_POA_FILE_SIZE_KB : MIN_FILE_SIZE_KB;

            return {
                file,
                status: validateFileSize(file.size, minSize),
            };
        },
        [validateFileSize],
    );

    const uploadFile = useCallback(
        async ({
            file: filesToUpload,
            documentType,
            tab,
            side,
            metadata,
            screen_id,
        }: UploadFileProps): Promise<VerificationFileState> => {
            const id = uuidv4();
            const file = Array.isArray(filesToUpload) ? filesToUpload[0] : filesToUpload;

            try {
                const res = getSizeFile(file as FileWithPreview, tab);

                if (allowedExtensions) {
                    const isValid = isValidExtension(file.name, allowedExtensions);

                    if (!isValid) {
                        const newFile = {
                            id,
                            tab,
                            uploadedFile: {
                                name: file.name,
                                preview: file.preview,
                                size: file.size,
                            },
                            documentType: '',
                            side: '',
                            status: {
                                error: true,
                                success: false,
                                message: t('validation.invalidFileExtension'),
                                loading: false,
                            },
                        } as unknown as VerificationFileState;

                        dispatch(docsVerificationSlice.actions.addFile(newFile));

                        return newFile;
                    }
                }

                if (res.status.error) {
                    const newFile = {
                        id,
                        tab,
                        uploadedFile: {
                            name: file.name,
                            preview: file.preview,
                            size: file.size,
                        },
                        documentType: '',
                        side: '',
                        status: res.status,
                    } as unknown as VerificationFileState;

                    dispatch(docsVerificationSlice.actions.addFile(newFile));

                    return newFile;
                }

                const newFile = {
                    id,
                    tab,
                    uploadedFile: {
                        name: file.name,
                        preview: file.preview,
                        size: file.size,
                    },
                    documentType: '',
                    side: '',
                    status: {
                        loading: true,
                        success: false,
                        error: false,
                    },
                } as unknown as VerificationFileState;

                dispatch(docsVerificationSlice.actions.addFile(newFile));

                let response: UploadDocumentResponse;

                if (sdkOptions?.isDemo) {
                    await sleep(1000);
                    response = uploadFileResponse({ documentType });
                } else if (documentType === DocsVerificationTypeEnum.LIVENESS) {
                    const files = filesToUpload as File[];

                    const options = {
                        maxSizeMB: 1,
                        maxWidthOrHeight: 1920,
                        useWebWorker: true,
                    };

                    let compressed = files;

                    try {
                        compressed = await Promise.all(
                            files.map((item) =>
                                imageCompression(item, options).then(
                                    (compressedFile) => compressedFile,
                                ),
                            ),
                        );
                    } catch {
                        /* empty */
                    }

                    response = await uploadLivenessDocumentSdk({
                        url: sdkOptions?.apiUrl as string,
                        id: sdkOptions?.id as string,
                        body: {
                            files: compressed,
                            metadata,
                            screen_id,
                        },
                    }).unwrap();
                } else {
                    const body: UploadDocumentRequest['body'] = {
                        file: file as FileWithPreview,
                        // @ts-expect-error test
                        document_type: documentType,
                        side,
                    };

                    response = await uploadDocumentSdk({
                        url: sdkOptions?.apiUrl as string,
                        id: sdkOptions?.id as string,
                        body,
                    }).unwrap();
                }

                const uploadedFileState = {
                    id: response.document_id,
                    tab,
                    uploadedFile: {
                        name: file.name,
                        preview: file.preview as string,
                        size: file.size,
                    },
                    documentType: response.detected_document_type,
                    side: response.detected_document_side,
                    isTwoSideDocument: response.detected_two_side_document,
                    country: response.detected_country,
                    status: {
                        loading: false,
                        success: true,
                        error: false,
                    },
                };

                setTimeout(() => {
                    dispatch(
                        docsVerificationSlice.actions.changeFile({
                            id: newFile.id as string,
                            file: uploadedFileState,
                        }),
                    );
                });

                return uploadedFileState;
            } catch (error) {
                let message = '';
                let code = '';

                if (Array.isArray(error?.data?.errors)) {
                    message = error.data.errors[0]?.message || 'common.pleaseTryAgainLater';
                    code = error.data.errors[0]?.code || undefined;
                } else {
                    message = error?.data?.message;
                    code = error?.data?.code;
                }

                if (code === FileErrorCodeEnum.CONSENT_REQUIRED) {
                    dispatch(
                        docsVerificationSlice.actions.setCurrentStep(
                            DocsVerificationStepEnum.START,
                        ),
                    );
                }

                const status = {
                    success: false,
                    loading: false,
                    error: true,
                    message: message || error?.error,
                    code,
                };

                const fileState = {
                    id,
                    uploadedFile: {
                        name: file?.name || 'file',
                        preview: file?.preview,
                        size: file?.size,
                    },
                    documentType: '',
                    side: '',
                    tab,
                    status,
                } as unknown as VerificationFileState;

                if (message?.includes(FileErrorCodeEnum.LIMIT_UPLOADING)) {
                    const successFileState = {
                        ...fileState,
                    };

                    successFileState.status.error = false;
                    successFileState.status.success = true;
                    successFileState.status.message = '';
                    successFileState.status.isFailedVerification = true;

                    dispatch(
                        docsVerificationSlice.actions.changeFile({
                            id,
                            file: successFileState,
                        }),
                    );

                    dispatch(docsVerificationSlice.actions.setIsFailedVerification(true));

                    return successFileState;
                }

                dispatch(
                    docsVerificationSlice.actions.changeFile({
                        id,
                        file: fileState,
                    }),
                );

                return fileState;
            }
        },
        [
            sdkOptions,
            uploadDocumentSdk,
            dispatch,
            t,
            allowedExtensions,
            uploadLivenessDocumentSdk,
            getSizeFile,
        ],
    );

    return { uploadFile };
};
